import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../../styles/designTokens";

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }

  .content-wrapper {
    width: 100%;
    max-width: 90em;
    margin: 0 auto;
    padding: 2em;
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    min-height: 0;

    @media (max-width: 768px) { padding: 1em; }
  }

  /* Botão Voltar */
  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    margin-bottom: 8px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    ${mono};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    width: fit-content;

    &:hover {
      opacity: 0.85;
      background: ${({ theme }) => surface(theme, 1)};
    }

    svg { font-size: 0.95em; }
  }

  /* Header do Fornecedor */
  .supplier-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 24px 0 8px 0;
    margin-bottom: 4px;
    flex-shrink: 0;

    .supplier-info-bar {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 16px;
      min-width: 0;

      .supplier-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${tokens.brandSoft};
        border-radius: ${tokens.radiusLg};
        flex-shrink: 0;

        svg {
          font-size: 22px;
          color: ${tokens.brand};
        }
      }

      .supplier-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;

        .supplier-eyebrow {
          ${eyebrow};
        }

        h2 {
          margin: 0;
          font-size: 1.45em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .supplier-badges {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;

          .badge {
            ${mono};
            padding: 3px 10px;
            border-radius: ${tokens.radiusPill};
            font-size: 0.62em;
            font-weight: 500;
            letter-spacing: 0.02em;
            background: ${({ theme }) => surface(theme, 1)};
            color: ${({ theme }) => theme.text};
            opacity: 0.85;
            border: 1px solid ${({ theme }) => hairline(theme)};

            &.supplier-tag {
              background: ${tokens.brandSoft};
              color: ${tokens.brand};
              opacity: 1;
              border-color: ${tokens.brandRing};
            }
          }
        }
      }
    }
  }

  /* Tabs de Navegação */
  .tabs-container {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;
      outline: none;

      svg { font-size: 0.95em; }

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }
    }
  }

  /* Timeline da Reforma Tributária */
  .reform-timeline {
    display: flex !important;
    gap: 8px;
    padding: 0;
    margin-bottom: 12px;
    overflow-x: auto;
    width: 100%;
    min-height: 56px;

    &::-webkit-scrollbar { height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 2px;
    }

    .timeline-item {
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 16px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex-shrink: 0;
      min-width: 100px;
      position: relative;
      outline: none;

      .timeline-year {
        display: block;
        font-size: 1.2em;
        font-weight: 600;
        font-family: 'Poppins', system-ui, sans-serif;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: -0.02em;
      }

      .timeline-label {
        display: block;
        font-size: 0.7em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        text-align: center;
        white-space: nowrap;
      }

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};

        .timeline-year { opacity: 0.85; }
        .timeline-label { opacity: 0.65; }
      }

      &.active {
        background: ${tokens.brandSoft};
        border: 1px solid ${tokens.brandRing};
        border-left: 3px solid ${tokens.brand};

        .timeline-year {
          color: ${tokens.brand};
          opacity: 1;
        }

        .timeline-label {
          color: ${({ theme }) => theme.text};
          opacity: 1;
        }
      }
    }
  }

  /* Reforma tab wrapper */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* KPI Grid */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    &.kpi-grid-4 {
      grid-template-columns: repeat(4, 1fr);
    }

    &.kpi-grid-3 {
      grid-template-columns: repeat(3, 1fr);
    }

    &.kpi-grid-2 {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 1400px) {
      grid-template-columns: repeat(3, 1fr);

      &.kpi-grid-4 {
        grid-template-columns: repeat(2, 1fr);
      }

      &.kpi-grid-3 {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);

      &.kpi-grid-4,
      &.kpi-grid-3,
      &.kpi-grid-2 {
        grid-template-columns: 1fr;
      }
    }
  }

  /* KPI Card */
  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.5em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.tax-icms .card-content .thumb { color: #f59e0b; opacity: 0.65; }
    &.tax-piscofins .card-content .thumb { color: #ef4444; opacity: 0.65; }
    &.tax-cbs .card-content .thumb { color: #f59e0b; opacity: 0.65; }
    &.tax-ibs .card-content .thumb { color: #3b82f6; opacity: 0.65; }
  }

  /* Section Row */
  .section-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    align-items: stretch;

    > .stats-card,
    > .chart-card {
      height: 360px;
    }

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .chart-card {
    .chart-container {
      padding: 16px;
    }
  }

  /* Stats Card */
  .stats-card {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 18px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      background: transparent;
      flex-shrink: 0;

      .header-title {
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          font-size: 0.95em;
          color: ${tokens.brand};
          opacity: 0.7;
        }

        h4 {
          margin: 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }
      }

      .header-badge {
        font-size: 0.6em;
        font-weight: 600;
        color: ${tokens.brand};
        background: ${tokens.brandSoft};
        padding: 3px 8px;
        border-radius: ${tokens.radiusSm};
        text-transform: uppercase;
        letter-spacing: 0.04em;
        ${mono};
      }

      .chart-legend {
        display: flex;
        gap: 16px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;

          .legend-color {
            width: 10px;
            height: 10px;
            border-radius: 3px;
          }
        }
      }
    }

    .donut-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 20px;
      gap: 30px;

      .donut-chart {
        position: relative;
        flex-shrink: 0;

        .donut-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;

          .donut-total {
            font-size: 16px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            line-height: 1;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .donut-label {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-transform: uppercase;
            margin-top: 4px;
          }
        }
      }

      .segments-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: 0;

        .segment-item {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .segment-header {
            display: flex;
            align-items: center;
            gap: 8px;

            .segment-color {
              width: 12px;
              height: 12px;
              border-radius: 2px;
              flex-shrink: 0;
            }

            .segment-name {
              flex: 1;
              font-size: 13px;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
            }

            .segment-revenue {
              font-size: 13px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }
          }

          .segment-details {
            display: flex;
            align-items: center;

            .segment-percentage {
              font-size: 11px;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              margin-left: 18px;
            }
          }

          .segment-bar {
            width: 100%;
            height: 4px;
            background: ${({ theme }) => theme.border};
            border-radius: 2px;
            overflow: hidden;
            margin-left: 18px;

            .segment-progress {
              height: 100%;
              border-radius: 2px;
              transition: width 0.3s ease;
            }
          }
        }
      }

      .categories-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-width: 0;

        .category-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: ${({ theme }) => theme.tableRow};
          border-radius: 12px;

          .category-color {
            width: 10px;
            height: 10px;
            border-radius: 3px;
            flex-shrink: 0;
          }

          .category-name {
            flex: 1;
            font-size: 13px;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
          }

          .category-count {
            font-size: 12px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.8;
          }
        }
      }
    }

    &.contact-card {
      .card-header {
        padding: 16px 20px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        .header-title {
          display: flex;
          align-items: center;
          gap: 10px;

          svg {
            font-size: 18px;
            color: ${({ theme }) => theme.primary};
          }

          h4 {
            margin: 0;
            font-size: 15px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }
      }

      .contact-content {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 16px;

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;

          svg {
            font-size: 18px;
            color: ${({ theme }) => theme.primary};
            opacity: 0.7;
            margin-top: 2px;
          }

          .contact-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;

            .contact-label {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .contact-value {
              font-size: 14px;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
            }
          }
        }
      }
    }
  }

  /* Conteúdo da Reforma */
  .reform-info-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .reform-info-item {
      padding: 16px;
      background: ${({ theme }) => theme.tableRow};
      border-radius: 12px;
      border-left: 3px solid ${({ theme }) => theme.primary};

      h5 {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin: 0 0 8px 0;
      }

      p {
        font-size: 13px;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
        margin: 0;
        line-height: 1.6;
      }
    }
  }

  /* ====== HISTÓRICO / MOVIMENTAÇÕES (padrão Product Detail) ====== */
  .history-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    height: 100%;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
      overflow: visible;
      height: auto;
    }
  }

  .sort-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 0.72em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    ${mono};
    letter-spacing: 0.03em;

    svg {
      font-size: 0.9em;
      opacity: 0.7;
    }

    &:hover {
      opacity: 0.9;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .download-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 24px;
    padding: 0 8px;
    background: ${({ theme }) => theme.buttonBg};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    cursor: pointer;
    transition: all 0.2s ease;

    svg { font-size: 12px; }

    &:hover {
      opacity: 1;
      background: ${({ theme }) => theme.tableRowHover};
      border-color: ${({ theme }) => theme.primary};
      color: ${({ theme }) => theme.primary};
    }
  }

  .history-table-wrapper {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;

    .history-header-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 20px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      background: ${({ theme }) => surface(theme, 0)};
      flex-shrink: 0;

      .header-title-group {
        display: flex;
        align-items: center;
        gap: 10px;

        .header-icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: ${tokens.radiusMd};
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.6;

          svg { font-size: 0.85em; }
        }

        h4 {
          margin: 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }
      }

      .header-meta {
        display: flex;
        align-items: center;
        gap: 12px;

        .history-count {
          font-size: 11px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .history-table {
    padding: 0;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.border};
      border-radius: 2px;
    }

    .table-header {
      display: grid;
      grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) auto;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.tableRow};
      border-bottom: 1px solid ${({ theme }) => theme.border};
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      position: sticky;
      top: 0;
      z-index: 2;
      align-items: center;

      > * {
        text-align: left;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > *:last-child { text-align: right; }
    }

    .table-row {
      display: grid;
      grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) auto;
      gap: 12px;
      min-width: 0;
      padding: 16px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border}20;
      transition: all 0.2s ease;
      align-items: center;

      &:hover {
        background: ${({ theme }) => theme.tableRowHover};
      }

      &:last-child {
        border-bottom: none;
      }

      > * {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        min-width: 0;
        overflow: hidden;
      }
      > *:last-child {
        justify-content: flex-end;
      }

      .product-info {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
        width: 100%;

        .product-color {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .product-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;

          .product-name {
            font-size: 13px;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .product-code {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }

      .quantity-badge {
        font-size: 12px;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
      }

      .value-text {
        font-size: 13px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      .value-highlight {
        font-size: 13px;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
      }

      .participacao-bar-container {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 80px;

        .participacao-bar {
          height: 3px;
          border-radius: 2px;
          min-width: 4px;
          background: ${({ theme }) => theme.primary};
          opacity: 0.5;
        }

        .participacao-value {
          font-size: 12px;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
        }
      }
    }

    .history-empty {
      padding: 56px 20px;
      text-align: center;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 13px;
      font-weight: 400;
    }

    .history-loader {
      padding: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 13px;

      .spinner {
        width: 14px;
        height: 14px;
        border: 2px solid ${({ theme }) => theme.border};
        border-top-color: ${({ theme }) => theme.primary};
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;
